<template>
  <div class="table-responsive" style="min-height: 550px;">
    <table class="table table-hover table-striped smart">
      <thead>
        <tr class="bg-secondary">
          <th class="hide-md-only">ITEM</th>

          <th v-for="col in visibleColumns" :key="col.key"
            :class="typeof col.class === 'string' ? col.class : undefined">
            {{ col.label }}
          </th>

          <th v-if="permittedActions.length">Actions</th>
        </tr>
      </thead>

      <!-- DATA: respuesta del backend lista -->
      <tbody v-if="statusData">
        <tr v-for="(row, index) in data" :key="rowKey ? rowKey(row, index) : index">
          <td data-label="ITEM" class="hide-md-only">
            {{ getGlobalIndex(index) }}
          </td>

          <td v-for="col in visibleColumns" :key="col.key" :data-label="col.label"
            :class="typeof col.class === 'function' ? col.class(row) : col.class">
            
            <span v-if="!col.render">{{ row[col.key] }}</span>

            <!-- Con render: evaluar UNA sola vez -->
            <template v-else>
              <template v-for="(rendered, _) in [resolveRender(col, row)]" :key="_">
                <!-- 1. Badge -->
                <span v-if="isRenderResult(rendered) && rendered.type === 'badge'"
                  :class="[`${rendered.variant}`, rendered.class ?? '']" 
                  :style="col.onClick ? 'cursor:pointer' : ''"
                  @click="col.onClick?.(row)">
                  {{ rendered.text }}
                    <i v-if="rendered.icon" 
                      :class="rendered.icon" 
                      v-tooltip="rendered.iconTooltip || undefined"
                      aria-hidden="true" />
                </span>

                <!-- 2. Texto con opciones (bold, class) -->
                <span v-else-if="isRenderResult(rendered) && rendered.type === 'text'"
                  :class="[rendered.bold ? 'font-weight-bold' : '', rendered.class ?? '']"
                  :style="col.onClick ? 'cursor:pointer' : ''" 
                  @click="col.onClick?.(row)">
                  {{ rendered.text }}
                </span>

                <!-- 3. HTML parcial (ej: fecha con hora en negrita) -->
                <span v-else-if="isRenderResult(rendered) && rendered.type === 'html'"
                  :style="col.onClick ? 'cursor:pointer' : ''" 
                  @click="col.onClick?.(row)" 
                  v-html="rendered.html" />

                <!-- 4. Texto plano (string | number) -->
                <span v-else :style="col.onClick ? 'cursor:pointer' : ''" 
                  @click="col.onClick?.(row)">
                  {{ rendered }}
                </span>

              </template>
            </template>
          </td>

          <td v-if="permittedActions.length" class="text-right">
            <div class="btn-group">
              <template v-for="(action, i) in getVisibleActions(row)" :key="i">

                <!-- Botón normal -->
                <button v-if="action.type !== 'dropdown'" 
                  class="btn btn-sm btn-default"
                  :class="typeof action.class === 'string' ? action.class : undefined"
                  @click="handleActionClick(action, row)" 
                  v-tooltip="getTooltip(action, row)">
                  <i :class="getIcon(action, row)"></i>
                </button>

                <!-- Dropdown -->
                <div v-else class="btn-group">
                  <button 
                    class="btn btn-sm btn-default dropdown-toggle" 
                    data-toggle="dropdown" aria-haspopup="true"
                    aria-expanded="false" 
                    :class="typeof action.class === 'string' ? action.class : undefined"
                    v-tooltip="getTooltip(action, row)">
                  </button>
                  <div class="dropdown-menu dropdown-menu-right">
                    <a v-for="(item, j) in action.items" 
                      :key="j" style="cursor: pointer;" 
                      class="dropdown-item"
                      :class="typeof item.class === 'string' ? item.class : undefined" 
                      @click="item.onClick(row)">
                      <i v-if="item.icon" :class="item.icon" class="mr-2"></i>
                      {{ item.label }}
                    </a>
                  </div>
                </div>

              </template>
            </div>
          </td>
        </tr>

        <tr v-if="data.length === 0">
          <td :colspan="colspanValue">There is no data</td>
        </tr>
      </tbody>

      <!-- SKELETON: esperando respuesta del backend (statusData = false) -->
      <tbody v-else>
        <tr v-for="n in 10" :key="n" class="skeleton-row">
          <td class="hide-md-only">
            <div class="sk sk-short" />
          </td>
          <td v-for="col in visibleColumns" :key="col.key"
            :class="typeof col.class === 'string' ? col.class : undefined">
            <div :class="['sk', skeletonWidth(col)]" />
          </td>
          <td v-if="permittedActions.length">
            <div class="sk sk-actions" />
          </td>
        </tr>
      </tbody>

    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { TableConfig, Column, RenderResult, DropdownItem } from './TypeTable'

const props = defineProps<{
  statusData: boolean           // false → skeleton | true → datos
  data: any[]
  config: TableConfig<any>
  startIndex?: number
  rowKey?: (row: any, index: number) => string | number
}>()

const visibleColumns = computed(() =>
  (props.config.columns ?? []).filter(col =>
    col.permission ? col.permission() : true
  )
)

const permittedActions = computed(() =>
  (props.config.actions ?? [])
    .filter((action): action is NonNullable<typeof action> => {
      if (!action || typeof action !== 'object') return false
      return action.permission ? action.permission() : true
    })
    .map(action => {
      if (action.type !== 'dropdown') return action
      return {
        ...action,
        items: (action.items ?? []).filter((item: DropdownItem<any>) =>
          item.permission ? item.permission() : true
        )
      }
    })
)

const getVisibleActions = (row: any) => {
  return permittedActions.value
    .map(action => {
      if (action.type === 'dropdown') {
        const visibleItems = (action.items ?? []).filter((item: DropdownItem<any>) =>
          item.show ? item.show(row) : true
        )
        if (visibleItems.length === 0) return null
        return { ...action, items: visibleItems }
      }
      const isVisible = action.show ? action.show(row) : true
      return isVisible ? action : null
    })
    .filter((action): action is NonNullable<typeof action> => action !== null)
}

const handleActionClick = (action: any, row: any) => {
  if (!action || action.type === 'dropdown') return
  if (typeof action.onClick === 'function') action.onClick(row)
  else console.warn('Acción inválida detectada:', action)
}

const getIcon = (action: any, row: any): string =>
  typeof action.icon === 'function' ? action.icon(row) : action.icon ?? ''


const getTooltip = (action: any, row: any): { text: string } | undefined => {
  if (!action.tooltip) return undefined
  const value = typeof action.tooltip === 'function' ? action.tooltip(row) : action.tooltip
  if (!value) return undefined
  return { text: value }    // ← objeto con .text en lugar de string plano
}

const colspanValue = computed(() => {
  let cols = visibleColumns.value.length + 1
  if (permittedActions.value.length) cols++
  return cols
})

const getGlobalIndex = (index: number) => {
  const offset = props.startIndex ?? 0
  return offset - index
}

const isRenderResult = (val: RenderResult | string | number | null): val is RenderResult =>
  val !== null && typeof val === 'object' && (val.type === 'badge' || val.type === 'text' || val.type === 'html')

const resolveRender = (col: Column<any>, row: any): RenderResult | string | number | null => {
  if (!col.render) return null
  return col.render(row)
}

// ─── Skeleton: ancho variable según tipo de columna ──────────────────────────

const skeletonWidth = (col: Column<any>): string => {
  if (col.skeletonSize) return `sk-${col.skeletonSize}`
  const len = col.label.length
  if (len <= 3) return 'sk-short'
  if (len <= 6) return 'sk-narrow'
  if (len <= 12) return 'sk-medium'
  return 'sk-wide'
}
</script>

<style>
.table.smart {
  font-family: 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  color: #333;
}

.table.smart tbody td {
  font-weight: 400;
}

.badge-custom {
  font-weight: 500;
  font-size: 0.75rem;
}

.btn-sm {
  font-size: 0.85rem;
}

.table-hover tbody tr:hover {
  background-color: #97a5d46c;
  box-shadow: inset 2px 0 0 #2C3E50;
}

@keyframes sk-shimmer {
  0% { background-position: -400px 0;  }
  100% { background-position: 400px 0;  }
}

.sk {
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 400px 100%;
  animation: sk-shimmer 1.4s ease infinite;
}

.sk-short  { width: 32px; }
.sk-narrow { width: 56px; }
.sk-medium { width: 96px; }
.sk-wide   { width: 140px;}
.sk-actions{ width: 72px; }

.skeleton-row td {
  overflow: hidden;
  padding-top: 14px;
  padding-bottom: 14px;
  vertical-align: middle;
}

.skeleton-row:hover {
  background-color: transparent !important;
  box-shadow: none !important;
}
</style>